import { parseISO } from 'date-fns';

import i18n from '@/core/i18n';
import { parseFormNumber, roundToPrecision } from '@/helpers/finance';
import {
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { formatDateToNonISOFormat, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { BillDetails, BillDetailsItemType } from '../types';
import { BillEntityTypeDTO } from '../types/dto';
import { UpdateBillRequest } from '../types/dto/request';
import {
  getMonthlySalaryAdjustment,
  isSalaryAdjustmentEmpty,
  SALARY_LINE_QUANTITY,
} from '../utils';
import { UpdateBillFormValues, UpdateBillItemFormValues } from '../validations';

const mapEntityType = (type: BillDetailsItemType): BillEntityTypeDTO => {
  switch (type) {
    case BillDetailsItemType.Project:
      return BillEntityTypeDTO.PROJECT;
    case BillDetailsItemType.Client:
      return BillEntityTypeDTO.CLIENT;
    case BillDetailsItemType.Unlinked:
      return BillEntityTypeDTO.NONE;
    case BillDetailsItemType.TimeOffType:
      return BillEntityTypeDTO.TIME_OFF_TYPE;
    case BillDetailsItemType.Overhead:
      return BillEntityTypeDTO.OVERHEAD;
    case BillDetailsItemType.Salary:
      return BillEntityTypeDTO.SALARY;
    case BillDetailsItemType.Adjustment:
      return BillEntityTypeDTO.ADJUSTMENT;
  }
};

const isCalculatedLine = (type: BillDetailsItemType) =>
  type === BillDetailsItemType.Project ||
  type === BillDetailsItemType.TimeOffType ||
  type === BillDetailsItemType.Salary ||
  type === BillDetailsItemType.Overhead;

const isFlatAmountLine = (type: BillDetailsItemType) =>
  type === BillDetailsItemType.Overhead || type === BillDetailsItemType.Adjustment;

const getInitialTimeOffSourceType = ({
  details,
  entityId,
  quantity,
  unitPrice,
  amount,
}: {
  details: BillDetails;
  entityId: number | null;
  quantity: string | null;
  unitPrice: string | null;
  amount: string;
}) => {
  const timeOffType = details.timeOffSources.find(source => source.timeOffTypeId === entityId);

  return timeOffType?.hoursSources.find(
    source =>
      source.hours === parseFormNumber(quantity ?? '') &&
      source.averageRate === parseFormNumber(unitPrice ?? '') &&
      source.amount === parseFormNumber(amount)
  )?.sourceType;
};

export const mapBillDetailsToUpdateFormValues = (details: BillDetails): UpdateBillFormValues => ({
  monthlySalaryEnabled: details.items.some(
    item =>
      item.entityType === BillDetailsItemType.Salary ||
      item.entityType === BillDetailsItemType.Adjustment
  ),
  period: {
    from: details.startDate ? parseISO(details.startDate) : undefined,
    to: details.endDate ? parseISO(details.endDate) : undefined,
  },
  dueDate: parseISO(details.dueDate),
  currencyCode: details.currencyCode,
  items: details.items
    .filter(
      item =>
        item.entityType !== BillDetailsItemType.Salary &&
        item.entityType !== BillDetailsItemType.Adjustment
    )
    .map(item => ({
      sourceItemId: item.id,
      entityType: item.entityType,
      entityId: item.entityId?.toString() ?? '',
      description: item.description ?? '',
      quantity: item.quantity ?? '',
      unitPrice: item.unitPrice ?? '',
      amount: item.amount,
      timeOffSourceType:
        item.entityType === BillDetailsItemType.TimeOffType
          ? getInitialTimeOffSourceType({
              details,
              entityId: item.entityId,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              amount: item.amount,
            })
          : undefined,
    })),
});

const mapFormItem = (item: UpdateBillItemFormValues) => {
  const amount = isCalculatedLine(item.entityType)
    ? roundToPrecision(
        parseFormNumber(item.quantity) * parseFormNumber(item.unitPrice),
        FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
      ).toString()
    : item.amount;

  return {
    entityType: mapEntityType(item.entityType),
    ...(item.entityId ? { entityId: Number(item.entityId) } : {}),
    ...(item.description.trim() ? { description: item.description.trim() } : {}),
    ...(!isFlatAmountLine(item.entityType) && item.quantity ? { quantity: item.quantity } : {}),
    ...(!isFlatAmountLine(item.entityType) && item.unitPrice
      ? {
          unitPrice: roundToPrecision(
            parseFormNumber(item.unitPrice),
            FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
          ).toString(),
        }
      : {}),
    amount,
  };
};

const getMonthlySalaryItems = ({
  items,
  monthlySalaryAmount,
}: {
  items: UpdateBillFormValues['items'];
  monthlySalaryAmount: string;
}) => {
  if (items.length === 0) {
    return [
      {
        entityType: BillEntityTypeDTO.SALARY,
        quantity: SALARY_LINE_QUANTITY.toString(),
        unitPrice: monthlySalaryAmount,
        amount: monthlySalaryAmount,
      },
    ];
  }

  const adjustment = getMonthlySalaryAdjustment({ items, monthlySalaryAmount });

  return isSalaryAdjustmentEmpty(adjustment)
    ? []
    : [
        {
          entityType: BillEntityTypeDTO.ADJUSTMENT,
          description: i18n.t('authenticated.bills:monthlySalary.adjustmentLabel'),
          amount: adjustment.toString(),
        },
      ];
};

export const mapUpdateBillFormToRequest = ({
  details,
  values,
  monthlySalaryAmount,
}: {
  details: BillDetails;
  values: UpdateBillFormValues;
  monthlySalaryAmount: string | null;
}): UpdateBillRequest => {
  if (!values.period.from || !values.period.to || !values.dueDate)
    throw new Error('Validated bill dates are required to build the update request');

  return {
    syncToken: details.syncToken,
    startDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(values.period.from)),
    endDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(values.period.to)),
    dueDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(values.dueDate)),
    currencyCode: values.currencyCode,
    items: [
      ...values.items.map(mapFormItem),
      ...(values.monthlySalaryEnabled && monthlySalaryAmount
        ? getMonthlySalaryItems({ items: values.items, monthlySalaryAmount })
        : []),
    ],
  };
};
